'use client';

import { useSearchParams } from 'next/navigation';
import Button from '~/components/buttons/Button';
import Flex from '~/components/flex/Flex';
import { Icon, IconName } from '~/components/icons/Icon';
import Modal from '~/components/modal/Modal';
import Text from '~/components/texts/Text';
import { SEARCH_PARAM_REGISTRATION_REQUEST_SUBMITTED_MODAL } from '~/constants';
import { AndercoreExternalLinks } from '~/constants/endpoints';
import { useCurrentLocale, useI18n } from '~/locales/client';
import { ButtonTypesEnum } from '~/types-and-enums/generalTypes';
import { colors, spacing } from '~/utils/tailwindUtils';

/**
 * Registration Request Submitted Modal Component
 *
 * Displays a confirmation screen after registration request is submitted,
 * showing KYC verification information and next steps.
 */
const RegistrationRequestSubmittedModal = () => {
  const searchParams = useSearchParams();
  const translate = useI18n();
  const locale = useCurrentLocale();

  const shouldOpen = searchParams.get(SEARCH_PARAM_REGISTRATION_REQUEST_SUBMITTED_MODAL) === 'true';

  const handleClose = (): void => {
    // Redirect to Andercore homepage (external redirect, so no need to clean up params)
    window.location.href = AndercoreExternalLinks[locale].andercoreHome;
  };

  const faqsUrl = AndercoreExternalLinks[locale].faqs;
  const linkedInUrl = AndercoreExternalLinks[locale].linkedIn;

  if (!shouldOpen) return null;

  return (
    <Modal
      isOpen={shouldOpen}
      closeModal={handleClose}
      size="lg"
      outsideClick={false}
      isCloseButtonHidden
      modalClassname="bg-gradient-to-br from-black/80 via-neutral-900/80 to-red-950/80"
      classname="relative w-full max-w-[520px] md:max-w-[560px] bg-gradient-to-br from-neutral-700/80 via-neutral-800/80 to-neutral-800/90 shadow-2xl p-6 md:p-8 lg:p-8 pb-6!"
    >
      <Flex className="flex-col gap-6 w-full mx-auto">
        {/* Close icon (top-right) */}
        <button
          type="button"
          aria-label={translate('general.close')}
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-full p-2 bg-white/5 hover:bg-white/10 transition-colors"
        >
          <Icon
            name={IconName.Cancel}
            size={spacing[5]}
            color={colors.white}
            colorMode="fill"
            customStrokeWidth={2}
          />
        </button>

        {/* Green Checkmark Circle */}
        <Flex className="items-center justify-center">
          <Flex className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-green-500 items-center justify-center">
            <Icon
              name={IconName.Check}
              size={spacing[10]}
              customStrokeWidth={2}
              color={colors.white}
            />
          </Flex>
        </Flex>

        {/* Title */}
        <Text className="text-2xl md:text-3xl font-bold text-center text-white">
          {translate('general.request-submitted')}
        </Text>

        {/* KYC Verification Alert Box */}
        <Flex className="bg-[#544821]/80 rounded-lg p-4 md:p-5 gap-3 flex-col items-center">
          <Flex className="items-center gap-2 ">
            <Icon
              name={IconName.Alert}
              size={spacing[5]}
              customStrokeWidth={1.75}
              color={colors.yellow[400]}
            />
            <Text className="text-sm md:text-base font-semibold text-yellow-400">
              {translate('general.kyc-verification-required')}
            </Text>
          </Flex>
          <Flex className="flex-col gap-1 flex-1">
            <Text className="text-sm md:text-base text-yellow-400/60 text-center">
              {translate('general.we-will-verify-your-identity')}
            </Text>
          </Flex>
        </Flex>

        {/* Main Information Text */}
        <Text className="font-normal text-white/80 text-center">
          {translate('general.thank-you-for-submitting-request', {
            andercore: <span className="font-semibold text-white">Andercore team</span>,
            kyc: <span className="text-green-400 font-semibold">{translate('general.kyc')}</span>,
          })}
        </Text>

        {/* What happens next? Section */}
        <Flex className="bg-white/5 rounded-lg p-4 md:p-6 flex-col gap-4">
          <Text className="text-sm md:text-base text-white">
            {translate('general.what-happens-next')}
          </Text>
          <Flex className="flex-col gap-3">
            {/* Step 1 */}
            <Flex className="items-center gap-3 justify-center">
              <Flex className="w-5 h-5 rounded-full bg-[#623e3e] items-center justify-center flex-shrink-0 mt-0.5">
                <Text className="text-red-500 text-2xs md:text-xs font-medium">1</Text>
              </Flex>
              <Text className="text-white/90 flex-1">
                {translate('general.our-team-will-verify')}
              </Text>
            </Flex>
            {/* Step 2 */}
            <Flex className="items-center gap-3 justify-center">
              <Flex className="w-5 h-5 rounded-full bg-[#623e3e] items-center justify-center flex-shrink-0 mt-0.5">
                <Text className="text-red-500 text-2xs md:text-xs font-medium">2</Text>
              </Flex>
              <Text className="text-white/90 flex-1">
                {translate('general.you-will-receive-an-email')}
              </Text>
            </Flex>
            {/* Step 3 */}
            <Flex className="items-center gap-3 justify-center">
              <Flex className="w-5 h-5 rounded-full bg-[#623e3e] items-center justify-center flex-shrink-0 mt-0.5">
                <Text className="text-red-500 text-2xs md:text-xs font-medium">3</Text>
              </Flex>
              <Text className="text-white/90 flex-1">
                {translate('general.once-approved-you-can-access')}
              </Text>
            </Flex>
          </Flex>
        </Flex>

        {/* Action Buttons */}
        <Flex className="flex-col md:flex-row gap-3 md:gap-4 w-full">
          {/* View FAQ Button */}
          <Button
            type="button"
            buttonStyle={ButtonTypesEnum.default}
            text={translate('general.view-faq')}
            icon={
              <Icon name={IconName.QuestionMarkCircle} size={spacing[5]} customStrokeWidth={1.75} color={colors.gray[400]} />
            }
            href={faqsUrl}
            buttonClassName="rounded-lg border border-white/10 bg-white/5 hover:opacity-80 md:hover:bg-white/10 text-white py-2.5 px-4 flex-1 w-full transition-opacity"
            textClassName="text-white/80"
          />

          {/* Follow on LinkedIn Button */}
          <Button
            type="button"
            buttonStyle={ButtonTypesEnum.default}
            text={translate('general.follow-on-linkedin')}
            icon={
              <Icon
                name={IconName.LinkedIn}
                size={spacing[4]}
                color={colors.white}
                colorMode="fill"
              />
            }
            href={linkedInUrl}
            buttonClassName="rounded-lg border border-blue-800 bg-blue-800/60 md:hover:bg-blue-600/80 text-white py-2.5 px-4 flex-1 w-full transition-opacity"
            textClassName="text-white/80"
          />
        </Flex>
      </Flex>
    </Modal>
  );
};

export default RegistrationRequestSubmittedModal;
